import {
    AccountClass,
    AccountSection,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAccountClassForType,
    isAccountType,
} from "@common/constants/domains/accounting/helpers";
import {
    EMPTY_GROUP_SORT_ORDER_SENTINEL,
    nestLeavesUnderSections,
    orderedPredefinedLeafTypes,
    resolveLeafType,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { accountMatchesForm1099BoxFilter } from "@common/constants/domains/accounting/form1099Box";
import { ExtendedAccount } from "@common/types/reports/types";
import {
    accountSortComparator,
    findFirstSortingLeafAccount,
    flatReportAccountSortComparator,
} from "@common/helpers/accounting";
import {
    ChartOfAccountsViewFilters,
    getDisplayedEntityIds,
} from "@common/types/domains/accounting/chartOfAccountsViewFilters";
import {
    getEntityRank,
    mergeGroupsForDisplay,
    MergedGroup,
} from "./entityGroupMerge";
import {
    getAccountDisplayKey,
    UNGROUPED_PARENT_IDENTITY,
} from "./chartRowDisplayKeys";
import {
    accountMatchesFilters,
    resolveGroupChain,
} from "./accountMatchesFilters";
import { accountMatchesCoaGroupKeys } from "./coaGroupFilter";
import {
    resolveMergedAccountSortOrder,
    shouldPruneEmptyGroup,
} from "./chartRowShaping";
import { compareScopeOrderTiebreak } from "./chartScopeOrdering";

// Hierarchical structure for ViewTable
export interface ChartOfAccountsRow {
    id: string;
    // For classification rows (top-level parents)
    classification?: AccountClass | AccountType;
    // For predefined balance-sheet section header rows (flag-on)
    section?: AccountSection;
    // For predefined leaf-type bucket rows (flag-on)
    predefinedType?: AccountType;
    // For group rows (workspace-level aggregator under a classification)
    group?: AccountGroup;
    // The group's constituents across every business the merged row spans,
    // ranked in entity-filter order. Chart Delete reads this to span businesses;
    // `entityIds` (own + descendant ids) is the wrong shape for a group delete.
    similarGroups?: AccountGroup[];
    groupSortOrder?: number;
    groupBalance?: number;
    groupDebit?: number;
    groupCredit?: number;
    // For account rows (leaves)
    account?: ExtendedAccount;
    entityIds?: number[];
    accountIds?: string[];
    similarAccounts?: ExtendedAccount[];
    groupKey?: string;
    lastItemInSubtype?: boolean;
    accounts?: ChartOfAccountsRow[];
}

/** Build expanded state with all expandable rows (those with children) expanded. */
export function getDefaultExpandedState(
    rows: ChartOfAccountsRow[],
): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    function visit(items: ChartOfAccountsRow[]) {
        for (const row of items) {
            if (row.accounts && row.accounts.length > 0) {
                state[row.id] = true;
                visit(row.accounts);
            }
        }
    }
    visit(rows);
    return state;
}

interface BuildHierarchicalChartOfAccountsParams {
    accounts?: ExtendedAccount[];
    groups?: AccountGroup[];
    filters: ChartOfAccountsViewFilters;
    orderMode: ChartOfAccountsOrderMode;
    showAccountNumbers?: boolean;
    withPredefinedSections?: boolean;
    // Set by callers that filter accounts themselves (outside `filters`) but
    // still want empty groups pruned — e.g. the apply preview's New/Custom
    // status/source filters, which have no equivalent in `filters`.
    hasExternalFilterActive?: boolean;
    accountIdsWithEnabledRules?: ReadonlySet<string>;
}

export const ChartOfAccountsOrderMode = {
    code: "code",
    sortOrder: "sortOrder",
} as const;

export type ChartOfAccountsOrderMode =
    (typeof ChartOfAccountsOrderMode)[keyof typeof ChartOfAccountsOrderMode];

interface MergedAccount {
    account: ExtendedAccount;
    entityIds: number[];
    accountIds: string[];
    similarAccounts: ExtendedAccount[];
    displayKey: string;
}

interface BuildAccountRowParams {
    merged: MergedAccount;
    nextMerged: MergedAccount | undefined;
}

type ClassificationKey = AccountClass | AccountType;

// Build hierarchical structure for ViewTable
export function buildHierarchicalChartOfAccounts({
    accounts,
    groups = [],
    filters,
    orderMode,
    showAccountNumbers = true,
    withPredefinedSections = false,
    hasExternalFilterActive = false,
    accountIdsWithEnabledRules,
}: BuildHierarchicalChartOfAccountsParams): ChartOfAccountsRow[] {
    if (!accounts) {
        return [];
    }
    const displayedEntityIds = getDisplayedEntityIds(filters);
    // Index groups by id BEFORE the account search filter so an account can
    // match on an ancestor custom-group name (see accountMatchesFilters).
    const groupNodesById = new Map(
        groups.map((group) => [
            group.id,
            {
                id: group.id,
                name: group.name,
                parentGroupId: group.parentGroupId,
            },
        ]),
    );
    let filteredAccounts = accounts;
    // Subtype is the one dimension outside the shared predicate: it is not
    // searched and does not survive Edit, so it stays chart-local.
    if (filters.accountSubtypes && filters.accountSubtypes.length > 0) {
        filteredAccounts = filteredAccounts.filter((a) =>
            filters.accountSubtypes!.includes(a.subtype),
        );
    }
    const form1099Boxes = filters.form1099Boxes;
    if (form1099Boxes && form1099Boxes.length > 0) {
        filteredAccounts = filteredAccounts.filter((a) =>
            accountMatchesForm1099BoxFilter(a, form1099Boxes),
        );
    }
    filteredAccounts = filteredAccounts.filter((account) => {
        const ancestorGroups = resolveGroupChain(
            account.parentGroupId,
            groupNodesById,
        );
        return (
            accountMatchesFilters(
                {
                    name: account.name,
                    codeText: account.codeText,
                    type: account.type,
                    entityId: account.entityId,
                    isDisabled: account.isDisabled,
                    isManuallyCreated: account.isManuallyCreated,
                    ancestorGroupNames: ancestorGroups.map(
                        (group) => group.name,
                    ),
                    hasRole: account.role != null,
                    hasRule: accountIdsWithEnabledRules?.has(account.id),
                },
                {
                    search: filters.search,
                    accountStatus: filters.accountStatus,
                    accountSource: filters.accountSource,
                    accountRolePresence: filters.accountRolePresence,
                    accountRulePresence: filters.accountRulePresence,
                    displayedEntityIds,
                },
            ) &&
            accountMatchesCoaGroupKeys({
                accountType: account.type,
                customGroupChainIds: ancestorGroups.map((group) => group.id),
                groupKeys: filters.coaTemplateGroupKeys,
            })
        );
    });

    const { groupsById, mergedGroupsByParentKey, groupKeyById } =
        mergeGroupsForDisplay({
            groups,
            selectedEntityIds: displayedEntityIds,
        });
    const selectedEntityRank = new Map(
        displayedEntityIds?.map((entityId, index) => [entityId, index]) ?? [],
    );
    const hasActiveFilter =
        hasExternalFilterActive ||
        !!filters.search ||
        !!filters.accountStatus ||
        !!filters.accountSource ||
        !!filters.accountRolePresence ||
        !!filters.accountRulePresence ||
        (filters.accountSubtypes?.length ?? 0) > 0 ||
        (filters.form1099Boxes?.length ?? 0) > 0 ||
        (filters.coaTemplateGroupKeys?.length ?? 0) > 0;

    // Parent identity is the parent group's display key, never its database id.
    const parentGroupIdentityOf = (account: ExtendedAccount): string =>
        account.parentGroupId && groupsById.has(account.parentGroupId)
            ? (groupKeyById.get(account.parentGroupId) ??
              UNGROUPED_PARENT_IDENTITY)
            : UNGROUPED_PARENT_IDENTITY;

    const bucketsByDisplayKey = new Map<string, ExtendedAccount[]>();
    for (const account of filteredAccounts) {
        const displayKey = getAccountDisplayKey({
            codeText: account.codeText,
            name: account.name,
            isDisabled: account.isDisabled,
            type: account.type,
            parentGroupIdentity: parentGroupIdentityOf(account),
        });
        const bucket = bucketsByDisplayKey.get(displayKey) ?? [];
        bucket.push(account);
        bucketsByDisplayKey.set(displayKey, bucket);
    }

    const buildMergedAccount = (
        displayKey: string,
        constituentsInInputOrder: ExtendedAccount[],
    ): MergedAccount => {
        const ranked = [...constituentsInInputOrder].sort(
            (a, b) =>
                getEntityRank(a.entityId, selectedEntityRank) -
                getEntityRank(b.entityId, selectedEntityRank),
        );
        const rankedFirst = ranked[0];
        const totals = constituentsInInputOrder.reduce(
            (acc, c) => ({
                debit: acc.debit + c.debit,
                credit: acc.credit + c.credit,
                balance: acc.balance + c.balance,
            }),
            { debit: 0, credit: 0, balance: 0 },
        );
        const parentGroupId =
            parentGroupIdentityOf(rankedFirst) === UNGROUPED_PARENT_IDENTITY
                ? null
                : (rankedFirst.parentGroupId ?? null);
        return {
            account: {
                ...rankedFirst,
                parentGroupId,
                sortOrder: resolveMergedAccountSortOrder(
                    ranked.map((c) => c.sortOrder),
                ),
                ...totals,
            },
            entityIds: ranked.map((c) => c.entityId),
            accountIds: ranked.map((c) => c.id),
            similarAccounts: ranked,
            displayKey,
        };
    };

    const accountsByGroupId = new Map<string, MergedAccount[]>();
    const ungroupedByClassification = new Map<
        ClassificationKey,
        MergedAccount[]
    >();

    for (const [displayKey, constituents] of bucketsByDisplayKey) {
        const merged = buildMergedAccount(displayKey, constituents);
        const bucketKey = parentGroupIdentityOf(constituents[0]);

        if (bucketKey === UNGROUPED_PARENT_IDENTITY) {
            const accountTypeValue = merged.account.type;
            const isOtherTypeBucket =
                isAccountType(accountTypeValue) &&
                (accountTypeValue === AccountType.otherIncome ||
                    accountTypeValue === AccountType.otherExpenses);
            const classificationKey: ClassificationKey = isOtherTypeBucket
                ? accountTypeValue
                : merged.account.class;

            if (!ungroupedByClassification.has(classificationKey)) {
                ungroupedByClassification.set(classificationKey, []);
            }
            ungroupedByClassification.get(classificationKey)!.push(merged);
        } else {
            const bucket = accountsByGroupId.get(bucketKey) ?? [];
            bucket.push(merged);
            accountsByGroupId.set(bucketKey, bucket);
        }
    }

    const buildAccountRow = ({
        merged,
        nextMerged,
    }: BuildAccountRowParams): ChartOfAccountsRow => ({
        id: merged.displayKey,
        account: merged.account,
        entityIds: merged.entityIds,
        accountIds: merged.accountIds,
        similarAccounts: merged.similarAccounts,
        groupKey: merged.displayKey,
        lastItemInSubtype:
            nextMerged !== undefined &&
            nextMerged.account.subtype !== merged.account.subtype,
    });

    const visitedGroupKeys = new Set<string>();
    const interleaveRows = (
        accountRows: ChartOfAccountsRow[],
        groupRows: ChartOfAccountsRow[],
    ) =>
        orderMode === ChartOfAccountsOrderMode.sortOrder
            ? interleaveBySortOrder(accountRows, groupRows)
            : interleaveByCode({
                  accountRows,
                  groupRows,
                  showAccountNumbers,
              });

    const buildGroupRow = (
        mergedGroup: MergedGroup,
    ): ChartOfAccountsRow | null => {
        if (visitedGroupKeys.has(mergedGroup.key)) {
            return null;
        }
        visitedGroupKeys.add(mergedGroup.key);

        const childGroups = mergedGroupsByParentKey.get(mergedGroup.key) ?? [];
        const childAccounts = accountsByGroupId.get(mergedGroup.key) ?? [];

        const childGroupRows: ChartOfAccountsRow[] = [];
        for (const child of childGroups) {
            const built = buildGroupRow(child);
            if (built) {
                childGroupRows.push(built);
            }
        }

        const sortedChildAccounts = [...childAccounts].sort((a, b) =>
            accountSortComparator(a.account, b.account, showAccountNumbers),
        );
        const accountRows = sortedChildAccounts.map((merged, index) =>
            buildAccountRow({
                merged,
                nextMerged: sortedChildAccounts[index + 1],
            }),
        );

        if (
            shouldPruneEmptyGroup({
                hasActiveFilter,
                childGroupRowCount: childGroupRows.length,
                childAccountRowCount: accountRows.length,
            })
        ) {
            return null;
        }

        const interleaved = interleaveRows(accountRows, childGroupRows);

        let debit = 0;
        let credit = 0;
        let balance = 0;
        const entityIds = new Set<number>();
        const accountIds: string[] = [];
        for (const group of mergedGroup.groups) {
            if (group.entityId !== undefined) {
                entityIds.add(group.entityId);
            }
        }
        for (const merged of childAccounts) {
            debit += merged.account.debit;
            credit += merged.account.credit;
            balance += merged.account.balance;
            for (const id of merged.entityIds) {
                entityIds.add(id);
            }
            for (const id of merged.accountIds) {
                accountIds.push(id);
            }
        }
        for (const childRow of childGroupRows) {
            debit += childRow.groupDebit ?? 0;
            credit += childRow.groupCredit ?? 0;
            balance += childRow.groupBalance ?? 0;
            for (const id of childRow.entityIds ?? []) {
                entityIds.add(id);
            }
            for (const id of childRow.accountIds ?? []) {
                accountIds.push(id);
            }
        }

        const groupRow: ChartOfAccountsRow = {
            id: `group:${mergedGroup.key}`,
            group: mergedGroup.group,
            similarGroups: mergedGroup.groups,
            groupSortOrder:
                mergedGroup.mergedSortOrder !==
                    EMPTY_GROUP_SORT_ORDER_SENTINEL ||
                mergedGroup.groups.every(
                    (group) =>
                        group.sortOrder === EMPTY_GROUP_SORT_ORDER_SENTINEL,
                )
                    ? mergedGroup.mergedSortOrder
                    : undefined,
            groupDebit: debit,
            groupCredit: credit,
            groupBalance: balance,
            entityIds: [...entityIds],
            accountIds,
            accounts: interleaved,
        };
        return groupRow;
    };

    const groupRowsByClassification = new Map<
        ClassificationKey,
        ChartOfAccountsRow[]
    >();

    for (const mergedGroup of mergedGroupsByParentKey.get(null) ?? []) {
        const classificationKey = resolveGroupClassificationKey(
            mergedGroup.group,
        );
        if (classificationKey === null) {
            continue;
        }
        const built = buildGroupRow(mergedGroup);
        if (!built) {
            continue;
        }
        if (!groupRowsByClassification.has(classificationKey)) {
            groupRowsByClassification.set(classificationKey, []);
        }
        groupRowsByClassification.get(classificationKey)!.push(built);
    }

    const visibleClassifications = new Set<ClassificationKey>([
        ...ungroupedByClassification.keys(),
        ...groupRowsByClassification.keys(),
    ]);

    if (withPredefinedSections) {
        const ungroupedByType = new Map<AccountType, MergedAccount[]>();
        for (const slices of ungroupedByClassification.values()) {
            for (const slice of slices) {
                const leafType = resolveLeafType(slice.account.type);
                if (!leafType) {
                    continue;
                }
                const bucket = ungroupedByType.get(leafType) ?? [];
                bucket.push(slice);
                ungroupedByType.set(leafType, bucket);
            }
        }

        const groupRowsByType = new Map<AccountType, ChartOfAccountsRow[]>();
        for (const rows of groupRowsByClassification.values()) {
            for (const row of rows) {
                const leafType = row.group
                    ? resolveLeafType(row.group.type)
                    : null;
                if (!leafType) {
                    continue;
                }
                const bucket = groupRowsByType.get(leafType) ?? [];
                bucket.push(row);
                groupRowsByType.set(leafType, bucket);
            }
        }

        const typeRows = new Map<AccountType, ChartOfAccountsRow>();
        for (const leafType of orderedPredefinedLeafTypes) {
            const ungroupedOfType = ungroupedByType.get(leafType) ?? [];
            const groupRowsOfType = groupRowsByType.get(leafType) ?? [];
            if (ungroupedOfType.length === 0 && groupRowsOfType.length === 0) {
                continue;
            }
            const ungroupedSorted = [...ungroupedOfType].sort((a, b) =>
                accountSortComparator(a.account, b.account, showAccountNumbers),
            );
            const ungroupedRows = ungroupedSorted.map((merged, index) =>
                buildAccountRow({
                    merged,
                    nextMerged: ungroupedSorted[index + 1],
                }),
            );
            typeRows.set(leafType, {
                id: `type:${leafType}`,
                predefinedType: leafType,
                accounts: interleaveRows(ungroupedRows, groupRowsOfType),
            });
        }

        return nestTypeRowsInSections(typeRows);
    }

    // Build hierarchical structure with explicit classification ordering
    const classificationOrder: Array<AccountClass | AccountType> = [
        AccountClass.assets,
        AccountClass.liabilities,
        AccountClass.equity,
        AccountClass.income,
        AccountClass.expenses,
        AccountType.otherIncome,
        AccountType.otherExpenses,
    ];

    return classificationOrder
        .filter((c) => visibleClassifications.has(c))
        .map((classification) => {
            const ungrouped =
                ungroupedByClassification.get(classification) ?? [];
            const groupRows =
                groupRowsByClassification.get(classification) ?? [];

            const ungroupedSorted = [...ungrouped].sort((a, b) =>
                accountSortComparator(a.account, b.account, showAccountNumbers),
            );
            const ungroupedRows = ungroupedSorted.map((merged, index) =>
                buildAccountRow({
                    merged,
                    nextMerged: ungroupedSorted[index + 1],
                }),
            );

            const interleaved = interleaveRows(ungroupedRows, groupRows);

            return {
                id: classification,
                classification,
                accounts: interleaved,
            };
        });
}

function resolveGroupClassificationKey(
    group: AccountGroup,
): ClassificationKey | null {
    const groupType = group.type;
    if (isAccountType(groupType)) {
        if (
            groupType === AccountType.otherIncome ||
            groupType === AccountType.otherExpenses
        ) {
            return groupType;
        }
        return getAccountClassForType(groupType);
    }
    return null;
}

// Empty groups sort at the end of their class bucket, after all coded rows.
const EMPTY_GROUP_SORT_KEY = Number.MAX_SAFE_INTEGER;

function getRowSortOrder(row: ChartOfAccountsRow): number {
    if (row.account) {
        return row.account.sortOrder;
    }
    if (row.group) {
        return row.groupSortOrder ?? row.group.sortOrder;
    }
    return EMPTY_GROUP_SORT_KEY;
}

function getRowName(row: ChartOfAccountsRow): string {
    if (row.account) {
        return row.account.name;
    }
    if (row.group) {
        return row.group.name;
    }
    return "";
}

function getRowAnchorAccount(
    row: ChartOfAccountsRow,
    showAccountNumbers: boolean,
): ExtendedAccount | undefined {
    return findFirstSortingLeafAccount({
        node: row,
        getChildren: (candidate) => candidate.accounts,
        getLeafAccount: (candidate) => candidate.account,
        showAccountNumbers,
    });
}

function compareRowsByFirstChild(
    a: ChartOfAccountsRow,
    b: ChartOfAccountsRow,
    showAccountNumbers: boolean,
): number {
    const aAnchorAccount = getRowAnchorAccount(a, showAccountNumbers);
    const bAnchorAccount = getRowAnchorAccount(b, showAccountNumbers);
    if (!aAnchorAccount || !bAnchorAccount) {
        if (aAnchorAccount !== bAnchorAccount) {
            return aAnchorAccount ? -1 : 1;
        }
    } else {
        const accountDifference = flatReportAccountSortComparator(
            aAnchorAccount,
            bAnchorAccount,
            showAccountNumbers,
        );
        if (accountDifference !== 0) {
            return accountDifference;
        }
    }
    const isAccountA = a.account !== undefined;
    const isAccountB = b.account !== undefined;
    if (isAccountA !== isAccountB) {
        return isAccountA ? -1 : 1;
    }
    const nameDifference = getRowName(a).localeCompare(getRowName(b));
    return nameDifference !== 0 ? nameDifference : a.id.localeCompare(b.id);
}

interface InterleaveByCodeParams {
    accountRows: ChartOfAccountsRow[];
    groupRows: ChartOfAccountsRow[];
    showAccountNumbers: boolean;
}

function interleaveByCode({
    accountRows,
    groupRows,
    showAccountNumbers,
}: InterleaveByCodeParams): ChartOfAccountsRow[] {
    return [...accountRows, ...groupRows].sort((a, b) =>
        compareRowsByFirstChild(a, b, showAccountNumbers),
    );
}

function interleaveBySortOrder(
    accountRows: ChartOfAccountsRow[],
    groupRows: ChartOfAccountsRow[],
): ChartOfAccountsRow[] {
    return [...accountRows, ...groupRows].sort((a, b) => {
        const orderA = getRowSortOrder(a);
        const orderB = getRowSortOrder(b);
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return compareScopeOrderTiebreak(
            {
                id: a.id,
                kind: a.account !== undefined ? "account" : "group",
                name: getRowName(a),
            },
            {
                id: b.id,
                kind: b.account !== undefined ? "account" : "group",
                name: getRowName(b),
            },
        );
    });
}

function nestTypeRowsInSections(
    typeRows: Map<AccountType, ChartOfAccountsRow>,
): ChartOfAccountsRow[] {
    return nestLeavesUnderSections<ChartOfAccountsRow>(
        orderedPredefinedLeafTypes,
        {
            createSectionNode: (section, pathKey) => ({
                id: `section:${pathKey}`,
                section,
                accounts: [],
            }),
            getSectionChildren: (sectionRow) => sectionRow.accounts ?? [],
            createLeafNode: (leafType) => typeRows.get(leafType) ?? null,
        },
    );
}
